import jwt_decode from "jwt-decode";
import { bundleLicenceAvailable } from '../../global/bundle';
import {
  JWT_TOKEN,
  ROLE_ADMIN,
  ROLE_COLLABORATOR,
  ROLE_PORTFOLIO_MANAGER,
  ROLE_PROJECT_MANAGER,
  ROLE_RESOURCE_MANAGER,
  ROLE_RESOURCE_COORDINATOR,
  ROLE_SYSTEM_ADMIN,
  ROLE_VIEWER,
  USER_ADMIN,
  USER_EDITOR,
} from "../../global/constants";
import {
  AI_LAB,
  HIDE_PORTFOLIO,
  TASK_MANAGEMENT,
  VISUAL_PLANNING,
  ZERO_ON_QUANTITY,
  LANGUAGE_TOGGLE,
} from "../../licenseData";

// Roles allowed to access metrics/portfolio features
const METRICS_ALLOWED_ROLES = [
  ROLE_PORTFOLIO_MANAGER,
  ROLE_PROJECT_MANAGER,
  ROLE_ADMIN,
];

export const allowFeature = (feature) => {
  if (!bundleLicenceAvailable(feature.trim())) return false;
  try {
    const { lic } = jwt_decode(localStorage.getItem(JWT_TOKEN));
    if (!lic) return false;

    return lic.includes(feature.trim());
  } catch (error) {
    //console.error(error);
  }
};

export const allowByRole = (...args) => {
  try {
    let isAllowed = false;
    const { roles } = jwt_decode(localStorage.getItem(JWT_TOKEN));

    if (!roles) return isAllowed;

    const rolesArray = Array.isArray(args) ? args : [args];
    isAllowed = rolesArray.some((role) => roles.includes(role));
    return isAllowed;
  } catch (error) {
    //console.error(error);
  }
};

export const findMinimunQuantity = () => {
  const { lic } = jwt_decode(localStorage.getItem(JWT_TOKEN));
  if (lic && lic.indexOf(ZERO_ON_QUANTITY) !== -1) {
    return 0;
  } else {
    return 1;
  }
};

export const visualPlanningPermission = () => {
  return allowFeature(VISUAL_PLANNING);
};

export const metricsPermission = () => {
  return (
    !allowFeature(HIDE_PORTFOLIO) &&
    allowByRole(...METRICS_ALLOWED_ROLES)
  );
};

/**
 * Robust implementation of metrics permission that handles JWT loading states properly.
 * Returns:
 * - null: JWT not yet loaded/decoded (pending state)
 * - true: User has permission to access metrics
 * - false: User does not have permission to access metrics
 */
export const robustMetricsPermission = () => {
  const isDev = process.env.NODE_ENV === 'development';
  
  try {
    const token = localStorage.getItem(JWT_TOKEN);
    
    // JWT not available yet - pending state
    if (!token) {
      return null;
    }
    
    // Try to decode JWT
    let decoded;
    try {
      decoded = jwt_decode(token);
    } catch (decodeError) {
      return null;
    }
    
    // Check if JWT has required fields
    if (!decoded || !decoded.roles || !decoded.lic) {
      return null;
    }
    
    // Check license feature (HIDE_PORTFOLIO)
    const hasPortfolioFeature = !decoded.lic.includes(HIDE_PORTFOLIO);
    
    // Check roles
    const hasRequiredRole = METRICS_ALLOWED_ROLES.some(
      (role) => decoded.roles.includes(role)
    );
    
    const result = hasPortfolioFeature && hasRequiredRole;
    
    return result;
  } catch (error) {
    if (isDev) console.error('[robustMetricsPermission] Unexpected error - returning null (pending)', error);
    return null;
  }
};

export const workloadPermission = () => {
  return allowByRole(
    ROLE_RESOURCE_MANAGER,
    ROLE_PROJECT_MANAGER,
    ROLE_PORTFOLIO_MANAGER,
    ROLE_ADMIN
  );
};

export const isViewer = () => allowByRole(ROLE_VIEWER);

export const isCollaborator = () => allowByRole(ROLE_COLLABORATOR);

/** Who may cancel an ongoing approval workflow (admins, portfolio + project managers). */
export const canCancelWorkflow = () =>
  allowByRole(ROLE_ADMIN, ROLE_SYSTEM_ADMIN, ROLE_PORTFOLIO_MANAGER, ROLE_PROJECT_MANAGER);

export const isAdmin = () => allowByRole(ROLE_ADMIN, ROLE_SYSTEM_ADMIN);

export const isSecurityAdmin = () =>
  allowByRole(ROLE_ADMIN, ROLE_SYSTEM_ADMIN, ROLE_PORTFOLIO_MANAGER);

export const isSecurityManager = () =>
  allowByRole(ROLE_PORTFOLIO_MANAGER, ROLE_PROJECT_MANAGER, ROLE_RESOURCE_MANAGER);

// Role-parameterized checks: is the GIVEN role eligible for a project tier?
// Unlike isSecurityAdmin/isSecurityManager (which test the logged-in user), these
// test a passed-in role — for filtering candidates by their own role.
// isSecurityAdminRole = Project Admin tier; isSecurityRole = Project Editor (any security role).
export const isSecurityAdminRole = (role) =>
  [ROLE_ADMIN, ROLE_SYSTEM_ADMIN, ROLE_PORTFOLIO_MANAGER, ROLE_PROJECT_MANAGER].includes(role);

export const isSecurityRole = (role) =>
  [ROLE_ADMIN, ROLE_SYSTEM_ADMIN, ROLE_PORTFOLIO_MANAGER, ROLE_PROJECT_MANAGER, ROLE_RESOURCE_MANAGER].includes(role);

export const isResourceManager = () => allowByRole(ROLE_RESOURCE_MANAGER);

export const isResourceCoordinator = () =>
  allowByRole(ROLE_RESOURCE_COORDINATOR);

export const myTasksPermission = () => {
  return (
    allowFeature(TASK_MANAGEMENT) &&
    allowByRole(
      ROLE_RESOURCE_MANAGER,
      ROLE_PORTFOLIO_MANAGER,
      ROLE_ADMIN,
      ROLE_VIEWER,
      // Resource Coordinator's WHOLE scope is the MLAP page — it must pass
      // this permission (routes + sidebar) while every other page hides
      // itself from the role (see LayoutPage links / SecuredRoute).
      ROLE_RESOURCE_COORDINATOR
    )
  );
};

export const libraryPermission = () =>
  !allowByRole(ROLE_VIEWER, ROLE_COLLABORATOR);

export const resourcePermission = () => {
  return allowByRole(ROLE_PORTFOLIO_MANAGER, ROLE_ADMIN, ROLE_RESOURCE_MANAGER);
};

export function isAllowedToEditProjectCollaborators() {
  return allowByRole(ROLE_PORTFOLIO_MANAGER, ROLE_ADMIN, ROLE_SYSTEM_ADMIN);
}

export const scopeGroupsPermission = isSecurityAdmin;

const DEFAULT_PERMISSIONS = {
  allowedToRead: true,
  allowedToUpdate: false,
  allowedToWrite: false,
  allowedToDelete: false,
};

export const DEFAULT_ADMIN_PERMISSIONS = {
  allowedToRead: true,
  allowedToUpdate: true,
  allowedToWrite: true,
  allowedToDelete: true,
};

const hasAnyProjectPermission = (permissions) =>
  Boolean(
    permissions &&
      (permissions.allowedToRead ||
        permissions.allowedToWrite ||
        permissions.allowedToUpdate ||
        permissions.allowedToDelete)
  );

export function computeUsersProjectPermissions(
  projectPermissions = {},
  projectId
) {
  // Elevated roles win, even when the user also carries a viewer/collaborator
  // role (matches the BE rule that admin wins among multiple roles).
  if (isAllowedToEditProjectCollaborators()) return DEFAULT_ADMIN_PERMISSIONS;

  // A pure collaborator/viewer stays read-only, even if they hold an admin
  // project role somewhere (the intent of bd923389f).
  if (allowByRole(ROLE_COLLABORATOR, ROLE_VIEWER)) return DEFAULT_PERMISSIONS;

  // A manager (project/resource/coordinator) with NO project role anywhere is
  // unrestricted across the whole portfolio — symmetric with userHasProjectRole
  // ("no role → sees all"). Once they hold a project role, they're scoped to it.
  const userHasAnyProjectRole = Object.values(projectPermissions || {}).some(
    hasAnyProjectPermission
  );
  if (!userHasAnyProjectRole) return DEFAULT_ADMIN_PERMISSIONS;

  if (projectId in projectPermissions) return projectPermissions[projectId];

  return DEFAULT_PERMISSIONS;
}

export function userHasProjectRole(project, projectPermissions = {}) {
  if (isAllowedToEditProjectCollaborators()) return true;

  const userHasAnyProjectRole = Object.values(projectPermissions || {}).some(
    hasAnyProjectPermission
  );
  if (!userHasAnyProjectRole) return true;

  return hasAnyProjectPermission(projectPermissions[project.id]);
}

/**
 * "Project Admin or Project Editor" tier for the given project's permission
 * object: the users who may type/paste into a manually-locked Resource Usage
 * cell directly (the BE mirrors this in UsageService.canDirectlyEditLockedUsages).
 * Single named source so every surface gates on the same rule.
 */
export function canDirectEditLockedCells(projectPermissions) {
  const roleName = computeRoleName(projectPermissions);
  return roleName === USER_ADMIN || roleName === USER_EDITOR;
}

export function computeRoleName(projectPermissions) {
  const isEditor =
    projectPermissions.allowedToRead &&
    projectPermissions.allowedToWrite &&
    projectPermissions.allowedToUpdate;
  if (isEditor && projectPermissions.allowedToDelete) return USER_ADMIN;
  else if (isEditor) return USER_EDITOR;
  return "viewer";
}

// Also drives the primary-role resolution of the collaborator pickers
// (`roleNames.find(r => ROLE_ORDER.includes(r))`): a role missing here loses to
// whatever else the user carries, which is why a Project Manager who was also a
// viewer resolved to "viewer" and stayed out of the Project Admin picker.
export const ROLE_ORDER = [
  "ROLE_SYSTEM_ADMIN",
  "ROLE_ADMIN",
  "ROLE_PORTFOLIO_MANAGER",
  "ROLE_PROJECT_MANAGER",
  "ROLE_RESOURCE_MANAGER",
  "ROLE_RESOURCE_COORDINATOR",
  "ROLE_COLLABORATOR",
  "ROLE_VIEWER",
];

export const ROLE_RANK = ROLE_ORDER.reduce((acc, r, i) => ((acc[r] = i), acc), {});

export const ROLE_LABELS = {
  ROLE_SYSTEM_ADMIN: "System App Admin",
  ROLE_ADMIN: "App Admin",
  ROLE_PORTFOLIO_MANAGER: "App Portfolio Manager",
  ROLE_PROJECT_MANAGER: "App Project Manager",
  ROLE_RESOURCE_MANAGER: "App Resource Manager",
  ROLE_RESOURCE_COORDINATOR: "App Resource Coordinator",
  ROLE_COLLABORATOR: "App Collaborator",
  ROLE_VIEWER: "App Viewer",
};

export const prettyRole = (role) =>
  (role || "")
    .replace(/^ROLE[_-]?/i, "")
    .toLowerCase()
    .split(/[_-]/g)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");

export const languageTogglePermission = () => allowFeature(LANGUAGE_TOGGLE);

/** AI Lab is restricted to Admin / System Admin + Portfolio Manager.
 * SYSTEM_ADMIN and ADMIN are already routed to their own rolesData
 * buckets in the Header, so this only needs to gate the shared "user"
 * bucket (which also covers Project/Resource Manager, Viewer and
 * Collaborator) on the Portfolio Manager role. */
export const hasAiLabAccessForRoles = (userRoles = []) =>
  allowFeature(AI_LAB) && userRoles.includes(ROLE_PORTFOLIO_MANAGER);
